import { Anchor, Box, Breadcrumbs, Text } from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import SenderVerificationAlert from '../../components/SenderVerificationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import WorkflowForm from '../../components/Workflows/WorkflowForm';
import { createFlowNode, generateEdgeId, getTypeOfFlow } from '../../constants/flowTypes';
import { CampaignTypes, Page } from '../../constants/pageTitles';
import { defaultCampaignGoals, normalizeCampaignGoals } from '../../helpers/campaignGoals';
import { friendlyGoogleErrorMessage } from '../../helpers/googleErrorUtils';
import useFetch from '../../helpers/useFetch';
import useCrossDomainCopyPage from '../../hooks/useCrossDomainCopyPage';

const page = `${Page['workflows']}`;

const WorkflowsNewPage = () => {
  const { domain } = useSelector((state) => state.domain);
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();

  // Use the cross-domain copy page hook
  const { from, fromDomain, clearFlowElements } = useCrossDomainCopyPage();

  const { mode } = queryString.parse(location.search);
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [campaign, setCampaign] = useState();
  const [segments, setSegments] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/segments?scope=internal`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch((error) => {
        setLoadError(error);
        setSegments([]);
      });
  }, [fetch, domain.accountId, domain.id]);

  useEffect(() => {
    if (segments === undefined) {
      return;
    }
    const defaultWorkflow = {
      name: 'New workflow',
      type: 'workflow',
      scope: 'marketing',
      reentrable: false,
      reentryCooldownInDays: 30,
      mode,
      segmentId: segments[0]?.id,
      scheduled: false,
      useOptimalTime: false,
      trigger: { type: 'event' },
      tags: [],
      flow: {
        nodes: [
          {
            id: 'start',
            type: 'start',
            position: {
              x: 300,
              y: 100,
            },
            data: {
              label: 'Start',
              custom: {
                key: 'start',
                config: [],
                values: {},
              },
            },
          },
        ],
        edges: [],
      },
      googleAssetGroup: {},
      goals: defaultCampaignGoals(domain),
    };
    const queryParams = new URLSearchParams(location.search);
    const flowType = queryParams.get('flowType');
    const flowElementId = parseInt(queryParams.get('flowElementId'));
    const flowElementName = queryParams.get('flowElementName');

    if (flowElementId && flowType && flowElementName) {
      const newNode = createFlowNode(flowType, flowElementId, flowElementName, {
        x: 276,
        y: 254,
      });

      defaultWorkflow.flow.nodes.push(newNode);
      defaultWorkflow.flow.edges.push({
        id: generateEdgeId('start', getTypeOfFlow(flowType) + flowElementId),
        source: 'start',
        target: getTypeOfFlow(flowType) + flowElementId,
      });
    }

    if (from) {
      // Use fromDomain if provided (cross-domain copy), otherwise use current domain.id (same domain copy)
      fetch(`/api/admin/accounts/${domain.accountId}/domains/${fromDomain || domain.id}/campaigns/${from}`)
        .then(({ campaign }) => {
          if (!fromDomain) {
            campaign.name = `Copy of ${campaign.name}`;
          }

          campaign.running = false;
          campaign.scheduled = false;
          campaign.aborted = false;
          campaign.googleAssetGroup = campaign.googleAssetGroup || {};
          delete campaign.scheduledAt;
          delete campaign.startedAt;
          delete campaign.finishedAt;
          delete campaign.abortedAt;
          delete campaign.id;

          // Clear selected elements when copying across domains
          const nodeTypes = [
            'sendEmail',
            'sendPushNotification',
            'sendTextMessage',
            'sendViberMessage',
            'sendFacebookAd',
            'sendGoogleAd',
            'sendWebhook',
            'sendWhatsappMessage',
          ];
          campaign = clearFlowElements(campaign, nodeTypes);

          setCampaign(normalizeCampaignGoals(campaign));
        })
        .catch(setLoadError);
    } else {
      setCampaign(defaultWorkflow);
    }
  }, [
    fetch,
    domain.accountId,
    domain.id,
    from,
    mode,
    segments,
    location,
    fromDomain,
    clearFlowElements,
    // defaultCampaignGoals derives its purchase actions from this map.
    domain.customEventConversions,
  ]);

  const create = (campaign) => {
    setUserError(undefined);
    return fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/campaigns`, {
      method: 'POST',
      body: JSON.stringify(campaign),
    })
      .then(() => {
        navigate(location.pathname.replace('/workflows/new', `/workflows?mode=${campaign.mode}`));
      })
      .catch((error) => {
        setUserError({ message: friendlyGoogleErrorMessage(error?.message) });
      });
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (campaign === undefined) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${domain.accountId}/domains/${domain.id}/analytics` },
    { title: page, href: `#/accounts/${domain.accountId}/domains/${domain.id}/workflows?mode=${campaign.mode}` },
    { title: 'New' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${CampaignTypes[campaign.mode]} | New`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <SenderVerificationAlert />

      <WorkflowForm campaign={campaign} saveCampaign={create} />

      {userError ? (
        <Box ta="right">
          <ErrorMessage message={userError.message} />
        </Box>
      ) : null}
    </>
  );
};

export default WorkflowsNewPage;
